import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels + the CANNON (win-multiplier-with-queue). A 2-step cascade where each winning
 * substep fires the chambered bullet and the queue advances with a GROWING multiplier:
 *
 *   reveal:  win 150 × 2 = 300  → cannon fires x2  → chamber x3, queue [x4, x5, x6, x7, x8]
 *   collapse: win 100 × 3 = 300 → cannon fires x3  → chamber x4, queue [x5, x6, x7, x8]
 *   collapse: no further win → end (total 600)
 *
 * The queue itself is reset to the ladder (chamber x2 + queue) by the CLIENT at spin-boundary start,
 * so the fixture only authors the FIRE per winning substep (`cannonFire(i)`), AFTER that substep's
 * `grid_win_highlight` (which carries baseWinAmount + multiplierApplied → "base × mult → final").
 * Win money is server-pre-multiplied. Total 600 (6× bet) — under the nice tier. No payline.
 */
export function baseFixtureReelCannon(): SpinResponse {
  const win1 = mask([10, 11, 15, 16]);       // L2 ×4
  const win2 = mask([5, 6, 10, 11]);         // L1 ×4 — rows 1-2 of cols 0,1 after the first tumble

  return spinFixture("reel-cannon")
    .balance("EUR", "100000")
    // --- seq 0: reveal — loadout revealed, then win 150×2 fires x2 ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("300").gridRows([
        ["L1", "L1", "L2", "H1", "L3"],
        ["L4", "L5", "L5", "L2", "L5"],
        ["L2", "L2", "L4", "L5", "L2"],
        ["L2", "L2", "L3", "L4", "L2"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win1,
        winAmount: "300", // 150 × 2 (server pre-multiplied)
        symbolIdentifier: "L2",
        symbolCount: "4",
        lines: [{ positionMask: win1, winAmount: "300", baseWinAmount: "150", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win1" });

      // Cannon fires the chambered x2 on this win, advances the queue.
      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire1" });

      const r = fx.removeSymbolsByMask(win1);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove1" });

      const t = fx.updateTotalWinMeter("300");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total1" });
    })
    // --- seq 1: collapse — win 100×3 fires x3 ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("600").gridRows([
        ["L5", "L3", "L2", "H1", "L3"],
        ["L1", "L1", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win2,
        winAmount: "300", // 100 × 3
        symbolIdentifier: "L1",
        symbolCount: "4",
        lines: [{ positionMask: win2, winAmount: "300", baseWinAmount: "100", multiplierApplied: "3" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win2" });

      const fire = cannonFire(1);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire2" });

      const r = fx.removeSymbolsByMask(win2);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove2" });

      const t = fx.updateTotalWinMeter("600");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total2" });
    })
    // --- seq 2: collapse — no further win, round ends ---
    .event("collapse", 2, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("600").gridRows([
        ["L2", "L5", "L2", "H1", "L3"],
        ["L4", "L3", "L5", "L2", "L5"],
        ["L5", "L3", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "600");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
